import React, { useState, useCallback, useEffect } from 'react';
import { Trophy, ZoomIn, ZoomOut, MoveHorizontal, Maximize2, X, CircleCheck as CheckCircle, Clock, TriangleAlert as AlertTriangle, TimerOff } from 'lucide-react';
import { MatchProgressionService, type MatchWithDetails, type BracketData } from '../services/MatchProgressionService';
import ShaderBackground from './ShaderBackground';

interface TournamentBracketProps {
  tournamentId: string;
  tournamentStatus: 'upcoming' | 'active' | 'completed';
  currentUserId?: string;
  primaryColor?: string;
  secondaryColor?: string;
  registrationCloseDate?: string | null;
  registrationLocked?: boolean;
  // New visual fields
  title?: string;
  logoUrl?: string | null;
  prizeDisplay?: string | null;
  sponsorLogos?: string[] | null;
  sponsorLinks?: string[] | null;
  backgroundType?: string | null;
  shaderPreset?: string | null;
  backgroundImageUrl?: string | null;
}

const TournamentBracket: React.FC<TournamentBracketProps> = ({
  tournamentId,
  tournamentStatus,
  currentUserId,
  primaryColor,
  secondaryColor,
  registrationCloseDate,
  registrationLocked,
  title,
  logoUrl,
  prizeDisplay,
  sponsorLogos,
  sponsorLinks,
  backgroundType,
  shaderPreset,
  backgroundImageUrl,
}) => {
  const accentColor = primaryColor || '#f97316';
  const highlightColor = secondaryColor || '#3b82f6';
  const effectiveShaderPreset = shaderPreset || 'hex_grid';
  const hasSponsorLogos = Array.isArray(sponsorLogos) && sponsorLogos.length > 0;

  const isRegistrationClosed = registrationLocked ||
    (registrationCloseDate ? new Date(registrationCloseDate) < new Date() : false);
  const isBracketTentative = !isRegistrationClosed;

  const [bracketData, setBracketData] = useState<BracketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scale, setScale] = useState(1.0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialScale, setInitialScale] = useState(0.7);

  useEffect(() => {
    loadBracket();

    const subscription = MatchProgressionService.subscribeToTournamentMatches(
      tournamentId,
      () => {
        loadBracket();
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [tournamentId]);

  useEffect(() => {
    if (bracketData) {
      const { totalRounds, bracketSize } = bracketData;
      const roundWidth = 180;
      const roundGap = 50;
      const isSmall = bracketSize <= 8;
      const svgWidth = Math.max(1200, (roundWidth + roundGap) * totalRounds * 2 + 500);
      const rawH = bracketSize * (isSmall ? 35 : 20) + 300;
      const svgHeight = Math.max(isSmall ? 600 : 900, rawH);

      const containerWidth = window.innerWidth * 0.9;
      const containerHeight = window.innerHeight * 0.7;

      const scaleToFitWidth = containerWidth / svgWidth;
      const scaleToFitHeight = containerHeight / svgHeight;

      const optimalScale = Math.min(scaleToFitWidth, scaleToFitHeight) * 0.9;

      setInitialScale(optimalScale);
      setScale(optimalScale);
      setPosition({ x: 0, y: 0 });
    }
  }, [bracketData]);

  const loadBracket = async () => {
    try {
      setLoading(true);
      const data = await MatchProgressionService.getTournamentBracket(tournamentId);
      setBracketData(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  const zoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.1, 2.0));
  };

  const zoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.1, 0.4));
  };

  const resetZoom = () => {
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setStartPosition({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - startPosition.x,
      y: e.clientY - startPosition.y
    });
  }, [isDragging, startPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading bracket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isNotGeneratedError = error.toLowerCase().includes('bracket not generated');

    if (isNotGeneratedError) {
      return (
        <div className="flex items-center justify-center h-96 text-white">
          <div className="text-center">
            <Trophy size={48} className="mx-auto mb-4 opacity-50" />
            <p>Bracket will be generated once enough participants join</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-96 text-white">
        <div className="text-center">
          <p className="text-red-400 mb-2">{error}</p>
          <button
            onClick={loadBracket}
            className="px-4 py-2 bg-orange-500 rounded hover:bg-orange-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!bracketData || bracketData.matches.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-white">
        <div className="text-center">
          <Trophy size={48} className="mx-auto mb-4 opacity-50" />
          <p>Bracket will be generated once enough participants join</p>
        </div>
      </div>
    );
  }

  const { matches, totalRounds, bracketSize } = bracketData;

  const roundWidth = 180;
  const roundGap = 50;
  const matchHeight = 32;
  const matchGap = 8;
  const bracketPadding = 60;

  const isSmallBracket = bracketSize <= 8;
  const baseSpacingValue = isSmallBracket ? 12 : 20;

  const svgWidth = Math.max(1200, (roundWidth + roundGap) * totalRounds * 2 + 500);
  const rawHeight = bracketSize * (isSmallBracket ? 35 : 20) + 300;
  const svgHeight = Math.max(isSmallBracket ? 600 : 900, rawHeight);

  const trophyAreaHeight = 80;

  const calculateSpacing = (round: number) => {
    return Math.pow(2, round) * baseSpacingValue;
  };

  const getRoundHeight = (round: number, matchesInRound: number) => {
    const spacing = calculateSpacing(round);
    return matchesInRound * ((matchHeight * 2) + matchGap) + (matchesInRound - 1) * spacing;
  };

  const getRoundX = (round: number, isRight: boolean = false) => {
    if (isRight) {
      return svgWidth - bracketPadding - (roundWidth + roundGap) * (round + 1) + roundGap;
    } else {
      return bracketPadding + (roundWidth + roundGap) * round;
    }
  };

  const getMatchStatusInfo = (match: MatchWithDetails) => {
    const p1HasEntry = !!match.participant1_entry;
    const p2HasEntry = !!match.participant2_entry;

    const p1Submitted = !!match.participant1_recording_id || (p1HasEntry && match.participant1_entry.status !== 'registered');
    const p2Submitted = !!match.participant2_recording_id || (p2HasEntry && match.participant2_entry.status !== 'registered');
    const bothSubmitted = p1Submitted && p2Submitted;

    const p1Scored = match.participant1_score !== null && match.participant1_score !== undefined;
    const p2Scored = match.participant2_score !== null && match.participant2_score !== undefined;
    const bothScored = p1Scored && p2Scored;

    const p1HasScore = p1Scored;
    const p2HasScore = p2Scored;
    const bothHaveScores = p1HasScore && p2HasScore;

    const isTimeout = match.match_status === 'forfeited' && (
      match.forfeit_reason === 'timeout_one_submission' ||
      match.forfeit_reason === 'timeout_both_no_submission'
    );
    const isManualForfeit = match.match_status === 'forfeited' && match.forfeit_reason === 'manual';
    const isDoubleTimeout = match.forfeit_reason === 'timeout_both_no_submission';

    const isMatchDone = match.match_status === 'completed' || match.match_status === 'forfeited';
    const showScores = bothHaveScores || (bothScored && bothSubmitted) || (isMatchDone && (p1HasScore || p2HasScore));

    return { p1Submitted, p2Submitted, bothSubmitted, showScores, p1Scored, p2Scored, isTimeout, isManualForfeit, isDoubleTimeout };
  };

  const renderParticipantBox = (
    match: MatchWithDetails,
    isParticipant1: boolean,
    x: number,
    y: number,
    width: number,
    isRight: boolean = false
  ) => {
    const participantId = isParticipant1 ? match.participant1_id : match.participant2_id;
    const participant = isParticipant1 ? match.participant1 : match.participant2;

    const isBye = !isParticipant1 && !match.participant2_id && match.participant1_id && match.match_status === 'completed';
    let username = participant?.username || 'TBD';
    if (isBye) {
      username = 'BYE';
    }

    const isForfeitNoSubmission = match.match_status === 'forfeited' && (match.forfeit_reason === 'timeout_both_no_submission' || match.forfeit_reason === 'timeout_one_submission');
    const didNotSubmit = isForfeitNoSubmission && !(isParticipant1 ? match.participant1_recording_id : match.participant2_recording_id);
    const score = didNotSubmit ? null : (isParticipant1 ? match.participant1_score : match.participant2_score) ?? null;

    const statusInfo = getMatchStatusInfo(match);
    const isSubmitted = isParticipant1 ? statusInfo.p1Submitted : statusInfo.p2Submitted;
    const isScored = isParticipant1 ? statusInfo.p1Scored : statusInfo.p2Scored;

    const hasScore = score !== null && score !== undefined;

    let isWinner = match.winner_id === participantId;
    if (statusInfo.showScores && !isWinner && hasScore) {
      const p1DidNotSubmit = isForfeitNoSubmission && !match.participant1_recording_id;
      const p2DidNotSubmit = isForfeitNoSubmission && !match.participant2_recording_id;
      const p1Score = p1DidNotSubmit ? null : match.participant1_score ?? null;
      const p2Score = p2DidNotSubmit ? null : match.participant2_score ?? null;
      if (p1Score !== null && p1Score !== undefined && p2Score !== null && p2Score !== undefined) {
        if (isParticipant1 && p1Score > p2Score) {
          isWinner = true;
        } else if (!isParticipant1 && p2Score > p1Score) {
          isWinner = true;
        }
      }
    }

    const isLoserByTimeout = (statusInfo.isTimeout || statusInfo.isDoubleTimeout) && match.winner_id !== participantId && participantId;
    const isWinnerByTimeout = statusInfo.isTimeout && match.winner_id === participantId;

    let borderColor: string;
    let textColor: string;
    let boxFill: string;

    if (isLoserByTimeout) {
      borderColor = '#ef4444';
      textColor = 'rgba(255,255,255,0.45)';
      boxFill = 'rgba(239,68,68,0.12)';
    } else if (isWinnerByTimeout) {
      borderColor = '#22c55e';
      textColor = '#0f172a';
      boxFill = '#ffffff';
    } else if (isWinner && statusInfo.showScores) {
      borderColor = '#22c55e';
      textColor = '#0f172a';
      boxFill = '#ffffff';
    } else if (participantId) {
      borderColor = 'rgba(255,255,255,0.35)';
      textColor = 'white';
      boxFill = 'rgba(255,255,255,0.14)';
    } else {
      borderColor = 'rgba(255,255,255,0.15)';
      textColor = 'rgba(255,255,255,0.35)';
      boxFill = 'rgba(255,255,255,0.05)';
    }

    const showTimeoutIcon = isLoserByTimeout && !statusInfo.isDoubleTimeout;
    const showDoubleTimeoutIcon = statusInfo.isDoubleTimeout && participantId;

    return (
      <g key={`participant-${match.id}-${isParticipant1 ? 'p1' : 'p2'}`}>
        {/* Glow effect for winners */}
        {isWinner && statusInfo.showScores && (
          <rect
            x={x - 2}
            y={y - 2}
            width={width + 4}
            height={matchHeight + 4}
            fill="none"
            stroke={borderColor}
            strokeWidth={4}
            rx={5}
            opacity={0.3}
          />
        )}
        {/* Main card */}
        <rect
          x={x}
          y={y}
          width={width}
          height={matchHeight}
          fill={boxFill}
          stroke={borderColor}
          strokeWidth={(isWinner && (statusInfo.showScores || isWinnerByTimeout)) ? 2.5 : participantId ? 1.5 : 1}
          rx={4}
        />
        {/* Left accent strip */}
        <rect
          x={x}
          y={y}
          width={4}
          height={matchHeight}
          fill={borderColor}
          rx={4}
          opacity={participantId ? 1 : 0.4}
        />

        {isRight ? (
          <>
            <text
              x={x + width - 8}
              y={y + matchHeight/2 + 5}
              fill={textColor}
              fontSize="14"
              textAnchor="end"
              fontWeight="medium"
              textDecoration={isLoserByTimeout ? 'line-through' : 'none'}
            >
              {username}
            </text>
            {statusInfo.showScores && hasScore && (
              <text
                x={x + 8}
                y={y + matchHeight/2 + 5}
                fill="#22c55e"
                fontSize="14"
                fontWeight="bold"
              >
                {score}
              </text>
            )}
            {(showTimeoutIcon || showDoubleTimeoutIcon) && (
              <g transform={`translate(${x + 8}, ${y + matchHeight/2 - 8})`}>
                <TimerOff size={16} color="#ef4444" />
              </g>
            )}
            {!showTimeoutIcon && !showDoubleTimeoutIcon && isSubmitted && !statusInfo.showScores && (
              <g transform={`translate(${x + 8}, ${y + matchHeight/2 - 8})`}>
                {isScored ? (
                  <Clock size={16} color="#fbbf24" />
                ) : (
                  <CheckCircle size={16} color="#22c55e" />
                )}
              </g>
            )}
          </>
        ) : (
          <>
            <text
              x={x + 12}
              y={y + matchHeight/2 + 5}
              fill={textColor}
              fontSize="13"
              fontWeight="500"
              textDecoration={isLoserByTimeout ? 'line-through' : 'none'}
            >
              {username}
            </text>
            {statusInfo.showScores && hasScore && (
              <>
                <rect
                  x={x + width - 38}
                  y={y + 5}
                  width={32}
                  height={matchHeight - 10}
                  fill="#22c55e30"
                  stroke="#22c55e80"
                  strokeWidth={1}
                  rx={3}
                />
                <text
                  x={x + width - 22}
                  y={y + matchHeight/2 + 5}
                  fill="#22c55e"
                  fontSize="12"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  {score}
                </text>
              </>
            )}
            {(showTimeoutIcon || showDoubleTimeoutIcon) && (
              <g transform={`translate(${x + width - 24}, ${y + matchHeight/2 - 8})`}>
                <TimerOff size={16} color="#ef4444" />
              </g>
            )}
            {!showTimeoutIcon && !showDoubleTimeoutIcon && isSubmitted && !statusInfo.showScores && (
              <g transform={`translate(${x + width - 24}, ${y + matchHeight/2 - 8})`}>
                {isScored ? (
                  <Clock size={16} color="#fbbf24" />
                ) : (
                  <CheckCircle size={16} color="#22c55e" />
                )}
              </g>
            )}
          </>
        )}

        {!statusInfo.showScores && statusInfo.bothSubmitted && !statusInfo.bothScored && (
          <g transform={`translate(${x + width/2 - 8}, ${y - 20})`}>
            <Clock size={16} color="#fbbf24" />
          </g>
        )}
      </g>
    );
  };

  const renderLine = (startX: number, startY: number, endX: number, endY: number, reverse: boolean = false) => {
    const path = reverse
      ? `M ${startX} ${startY} H ${startX - (startX - endX) / 2} V ${endY} H ${endX}`
      : `M ${startX} ${startY} H ${startX + (endX - startX) / 2} V ${endY} H ${endX}`;
    return (
      <g key={`line-${startX}-${startY}-${endX}-${endY}`}>
        {/* Glow layer */}
        <path d={path} stroke="rgba(255,255,255,0.10)" strokeWidth={5} fill="none" />
        {/* Main line */}
        <path d={path} stroke="rgba(255,255,255,0.40)" strokeWidth={1.5} fill="none" />
      </g>
    );
  };

  const renderRoundTitle = (round: number, x: number) => {
    const getRoundName = (roundNum: number, totalRounds: number): string => {
      if (totalRounds === 1) return 'Semi-Finals';
      if (totalRounds === 2) return ['Quarter-Finals', 'Semi-Finals'][roundNum] || '';
      if (totalRounds === 3) return ['Round of 8', 'Quarter-Finals', 'Semi-Finals'][roundNum] || '';
      if (totalRounds === 4) return ['Round of 16', 'Round of 8', 'Quarter-Finals', 'Semi-Finals'][roundNum] || '';
      if (totalRounds === 5) return ['Round of 32', 'Round of 16', 'Round of 8', 'Quarter-Finals', 'Semi-Finals'][roundNum] || '';
      if (totalRounds === 6) return ['Round of 64', 'Round of 32', 'Round of 16', 'Round of 8', 'Quarter-Finals', 'Semi-Finals'][roundNum] || '';
      return `Round ${roundNum + 1}`;
    };

    const label = getRoundName(round, totalRounds);
    const cx = x + roundWidth / 2;
    const cy = bracketPadding - 22;
    const pillW = Math.min(roundWidth - 8, label.length * 8 + 24);
    const pillH = 22;

    return (
      <g key={`round-title-${round}-${x}`}>
        <rect
          x={cx - pillW / 2}
          y={cy - pillH / 2}
          width={pillW}
          height={pillH}
          rx={pillH / 2}
          fill="rgba(255,255,255,0.12)"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth={1}
        />
        <text
          x={cx}
          y={cy + 5}
          fill="rgba(255,255,255,0.85)"
          fontSize="11"
          textAnchor="middle"
          fontWeight="700"
          letterSpacing="0.5"
        >
          {label.toUpperCase()}
        </text>
      </g>
    );
  };

  const matchesByRound = matches.reduce((acc, match) => {
    if (!acc[match.round_number]) {
      acc[match.round_number] = [];
    }
    acc[match.round_number].push(match);
    return acc;
  }, {} as Record<number, MatchWithDetails[]>);

  const totalRoundsActual = Math.max(...matches.map(m => m.round_number));
  const elements: JSX.Element[] = [];

  // Determine which matches belong to left vs right bracket halves
  const finalsMatch = matchesByRound[totalRoundsActual]?.[0];
  const leftBranchMatchIds = new Set<string>();
  const rightBranchMatchIds = new Set<string>();

  if (finalsMatch && totalRoundsActual > 1) {
    // Trace back from finals to determine left and right branches
    const semiFinalsMatches = matchesByRound[totalRoundsActual - 1] || [];

    if (semiFinalsMatches.length >= 2) {
      // First semi-final match feeds the left side of bracket
      const leftSemiFinal = semiFinalsMatches[0];
      // Second semi-final match feeds the right side of bracket
      const rightSemiFinal = semiFinalsMatches[1];

      // Recursively mark all matches that lead to left semi-final
      const markBranch = (matchId: string, targetSet: Set<string>) => {
        targetSet.add(matchId);
        const feedingMatches = matches.filter(m => m.next_match_id === matchId);
        feedingMatches.forEach(m => markBranch(m.id, targetSet));
      };

      markBranch(leftSemiFinal.id, leftBranchMatchIds);
      markBranch(rightSemiFinal.id, rightBranchMatchIds);
    }
  }

  // Render left side of bracket (going towards finals)
  for (let round = 1; round < totalRoundsActual; round++) {
    const roundMatches = (matchesByRound[round] || []).filter(m => leftBranchMatchIds.has(m.id));
    const matchesInRound = roundMatches.length;

    if (matchesInRound === 0) continue;

    const spacing = calculateSpacing(round - 1);
    const totalRoundHeight = getRoundHeight(round - 1, matchesInRound);
    const startY = (svgHeight - totalRoundHeight) / 2;

    const isRightSide = false;
    const roundX = getRoundX(round - 1, isRightSide);

    elements.push(renderRoundTitle(round - 1, roundX));

    roundMatches.forEach((match, matchIndex) => {
      const matchY = startY + matchIndex * ((matchHeight * 2) + matchGap + spacing);

      if (match.participant1_id) {
        elements.push(renderParticipantBox(match, true, roundX, matchY, roundWidth - 10, isRightSide));
      }

      if (match.participant2_id) {
        elements.push(renderParticipantBox(match, false, roundX, matchY + matchHeight + matchGap, roundWidth - 10, isRightSide));
      }

      if (match.next_match_id && round < totalRoundsActual) {
        const nextMatch = matches.find(m => m.id === match.next_match_id);
        if (nextMatch) {
          const nextRoundMatches = (matchesByRound[nextMatch.round_number] || []).filter(m =>
            nextMatch.round_number === totalRoundsActual ? true : leftBranchMatchIds.has(m.id)
          );
          const nextMatchIndex = nextRoundMatches.findIndex(m => m.id === nextMatch.id);

          if (nextMatchIndex !== -1) {
            let connectorY: number;
            if (nextMatch.round_number === totalRoundsActual) {
              const finalsMatchY = svgHeight / 2 - (matchHeight + matchGap / 2) + trophyAreaHeight / 2;
              connectorY = finalsMatchY + matchHeight + matchGap / 2;
            } else {
              const nextRoundSpacing = calculateSpacing(nextMatch.round_number - 1);
              const nextRoundTotalHeight = getRoundHeight(nextMatch.round_number - 1, nextRoundMatches.length);
              const nextRoundStartY = (svgHeight - nextRoundTotalHeight) / 2;
              const nextMatchY = nextRoundStartY + nextMatchIndex * ((matchHeight * 2) + matchGap + nextRoundSpacing);
              connectorY = nextMatchY + matchHeight + matchGap / 2;
            }

            elements.push(renderLine(
              roundX + roundWidth - 10,
              matchY + matchHeight + matchGap / 2,
              nextMatch.round_number === totalRoundsActual ? svgWidth / 2 - roundWidth / 2 : getRoundX(nextMatch.round_number - 1, isRightSide),
              connectorY
            ));
          }
        }
      }
    });
  }

  // Render right side of bracket (going towards finals)
  for (let round = 1; round < totalRoundsActual; round++) {
    const roundMatches = (matchesByRound[round] || []).filter(m => rightBranchMatchIds.has(m.id));
    const matchesInRound = roundMatches.length;

    if (matchesInRound === 0) continue;

    const spacing = calculateSpacing(round - 1);
    const totalRoundHeight = getRoundHeight(round - 1, matchesInRound);
    const startY = (svgHeight - totalRoundHeight) / 2;

    const isRightSide = true;
    const roundX = getRoundX(round - 1, isRightSide);

    elements.push(renderRoundTitle(round - 1, roundX));

    roundMatches.forEach((match, matchIndex) => {
      const matchY = startY + matchIndex * ((matchHeight * 2) + matchGap + spacing);

      if (match.participant1_id) {
        elements.push(renderParticipantBox(match, true, roundX, matchY, roundWidth - 10, isRightSide));
      }

      if (match.participant2_id) {
        elements.push(renderParticipantBox(match, false, roundX, matchY + matchHeight + matchGap, roundWidth - 10, isRightSide));
      }

      if (match.next_match_id && round < totalRoundsActual) {
        const nextMatch = matches.find(m => m.id === match.next_match_id);
        if (nextMatch) {
          const nextRoundMatches = (matchesByRound[nextMatch.round_number] || []).filter(m =>
            nextMatch.round_number === totalRoundsActual ? true : rightBranchMatchIds.has(m.id)
          );
          const nextMatchIndex = nextRoundMatches.findIndex(m => m.id === nextMatch.id);

          if (nextMatchIndex !== -1) {
            let connectorY: number;
            if (nextMatch.round_number === totalRoundsActual) {
              const finalsMatchY = svgHeight / 2 - (matchHeight + matchGap / 2) + trophyAreaHeight / 2;
              connectorY = finalsMatchY + matchHeight + matchGap / 2;
            } else {
              const nextRoundSpacing = calculateSpacing(nextMatch.round_number - 1);
              const nextRoundTotalHeight = getRoundHeight(nextMatch.round_number - 1, nextRoundMatches.length);
              const nextRoundStartY = (svgHeight - nextRoundTotalHeight) / 2;
              const nextMatchY = nextRoundStartY + nextMatchIndex * ((matchHeight * 2) + matchGap + nextRoundSpacing);
              connectorY = nextMatchY + matchHeight + matchGap / 2;
            }

            const lineEndX = nextMatch.round_number === totalRoundsActual
              ? svgWidth / 2 + roundWidth / 2 - 10
              : getRoundX(nextMatch.round_number - 1, isRightSide) + roundWidth - 10;
            elements.push(renderLine(
              roundX,
              matchY + matchHeight + matchGap / 2,
              lineEndX,
              connectorY,
              true
            ));
          }
        }
      }
    });
  }

  if (finalsMatch) {
    const roundX = svgWidth / 2 - roundWidth / 2;
    const matchY = svgHeight / 2 - (matchHeight + matchGap / 2) + trophyAreaHeight / 2;
    const finalsW = roundWidth + 20;
    const finalsX = svgWidth / 2 - finalsW / 2;

    // Gold glow behind finals
    elements.push(
      <rect
        key="finals-glow"
        x={finalsX - 8}
        y={matchY - 8}
        width={finalsW + 16}
        height={(matchHeight * 2) + matchGap + 16}
        rx={10}
        fill="#eab30820"
        stroke="#eab30850"
        strokeWidth={2}
      />
    );

    // Trophy icon area
    elements.push(
      <g key="finals-trophy" transform={`translate(${svgWidth / 2 - 20}, ${matchY - 52})`}>
        <Trophy size={40} color="#eab308" />
      </g>
    );

    // FINALS pill banner
    const finalsLabel = 'FINALS';
    const pillW = finalsLabel.length * 8 + 28;
    const cy = bracketPadding - 22;
    elements.push(
      <g key="finals-title">
        <rect
          x={svgWidth / 2 - pillW / 2}
          y={cy - 11}
          width={pillW}
          height={22}
          rx={11}
          fill="#eab30830"
          stroke="#eab30890"
          strokeWidth={1.5}
        />
        <text
          x={svgWidth / 2}
          y={cy + 5}
          fill="#eab308"
          fontSize="11"
          textAnchor="middle"
          fontWeight="700"
          letterSpacing="1"
        >
          {finalsLabel}
        </text>
      </g>
    );

    if (finalsMatch.participant1_id) {
      elements.push(renderParticipantBox(finalsMatch, true, finalsX, matchY, finalsW - 10, false));
    }

    if (finalsMatch.participant2_id) {
      elements.push(renderParticipantBox(finalsMatch, false, finalsX, matchY + matchHeight + matchGap, finalsW - 10, false));
    }
  }

  const showShaderBg = backgroundType === 'shader' && shaderPreset;
  const showImageBg = backgroundType === 'image' && backgroundImageUrl;

  return (
    <div
      className={isFullscreen ? "fixed inset-0 z-50 flex flex-col" : "flex flex-col"}
    >
      {isBracketTentative && (
        <div className="mx-4 mt-4 px-4 py-3 rounded-lg flex items-center gap-3"
             style={{ backgroundColor: '#f59e0b20', border: '1px solid #f59e0b50' }}>
          <AlertTriangle size={20} className="text-amber-400 flex-shrink-0" />
          <div>
            <div className="font-semibold text-amber-400">Tentative Bracket</div>
            <div className="text-sm text-white/70">
              {registrationCloseDate
                ? `Registration closes on ${new Date(registrationCloseDate).toLocaleDateString()}. The bracket may change as new participants join.`
                : 'Registration is still open. The bracket may change as new participants join.'}
            </div>
          </div>
        </div>
      )}

      {/* Bracket header: logo + title + prize callout + zoom controls */}
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Tournament logo"
              className="h-10 w-auto object-contain flex-shrink-0"
            />
          )}
          {title && (
            <span
              className="font-bold text-lg truncate"
              style={{ color: accentColor }}
            >
              {title}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {prizeDisplay && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{
                backgroundColor: '#eab30820',
                border: '1px solid #eab30860',
                color: '#eab308'
              }}
            >
              <Trophy size={14} />
              {prizeDisplay}
            </div>
          )}
          <button
            onClick={zoomOut}
            className="p-2 text-white rounded-full transition-colors"
            style={{ backgroundColor: `${accentColor}40` }}
            title="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          <button
            onClick={zoomIn}
            className="p-2 text-white rounded-full transition-colors"
            style={{ backgroundColor: `${accentColor}40` }}
            title="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
          <button
            onClick={resetZoom}
            className="p-2 text-white rounded-full transition-colors"
            style={{ backgroundColor: `${accentColor}40` }}
            title="Reset view"
          >
            <MoveHorizontal size={18} />
          </button>
          <span className="text-xs text-white/60">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={toggleFullscreen}
            className="p-2 text-white rounded-full transition-colors ml-1"
            style={{ backgroundColor: accentColor }}
            title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
          >
            {isFullscreen ? <X size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      <div
        className={`relative overflow-hidden rounded-lg mx-4 ${isFullscreen ? "flex-1" : "h-[70vh]"}`}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: showShaderBg || showImageBg ? 'transparent' : '#0d1117',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        {/* Animated shader background */}
        {showShaderBg && (
          <div className="absolute inset-0 z-0">
            <ShaderBackground
              preset={shaderPreset!}
              primaryColor={accentColor}
              secondaryColor={highlightColor}
            />
            {/* Subtle dark veil so bracket content stays readable */}
            <div className="absolute inset-0 bg-black/40" />
          </div>
        )}

        {/* Static image background */}
        {showImageBg && (
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImageUrl})` }}
          >
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}

        <div
          className="relative z-10 w-full h-full overflow-auto"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              minWidth: "100%",
              minHeight: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <div
              style={{
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                transformOrigin: "50% 50%",
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              }}
            >
              <svg
                width={svgWidth}
                height={svgHeight}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                style={{
                  display: 'block',
                  margin: '0 auto'
                }}
              >
                <rect
                  x={0}
                  y={0}
                  width={svgWidth}
                  height={svgHeight}
                  fill="transparent"
                />

                {elements}

              </svg>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-4 left-4 px-4 py-3 rounded-lg text-sm text-white space-y-2 z-20"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)' }}
        >
          <div className="flex items-center gap-3">
            <CheckCircle size={16} className="text-green-400" />
            <span>Submitted</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-yellow-400" />
            <span>Evaluating</span>
          </div>
          <div className="flex items-center gap-3">
            <TimerOff size={16} className="text-red-400" />
            <span>Timed Out</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-green-400 rounded-sm"></div>
            <span>Scores visible when both evaluated</span>
          </div>
        </div>

        <div
          className="absolute bottom-4 right-4 px-4 py-2 rounded-full text-sm text-white/70 z-20"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)' }}
        >
          Use mouse or zoom controls to navigate
        </div>
      </div>

      {/* Sponsor logo strip */}
      {hasSponsorLogos && (
        <div
          className="mx-4 mt-2 mb-1 px-4 py-3 rounded-lg flex items-center gap-6 overflow-x-auto"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <span className="text-xs text-white/40 uppercase tracking-widest flex-shrink-0">Sponsors</span>
          {sponsorLogos!.map((url, i) => {
            const link = sponsorLinks?.[i]?.trim();
            const img = (
              <img
                src={url}
                alt={`Sponsor ${i + 1}`}
                className="h-8 w-auto object-contain flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
              />
            );
            return link ? (
              <a
                key={i}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
                title={link}
              >
                {img}
              </a>
            ) : (
              <span key={i} className="flex-shrink-0">{img}</span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TournamentBracket;
